import { SortingState } from "@tanstack/react-table"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { useGetReferrals } from "../../hooks/useGetReferrals"
import { ReferralTable } from "../ReferralTable"

const sortParamKey: Record<string, string> = {
  status: "referral_status_order",
  referral_complete: "referral_complete",
  referral_date: "referral_date",
  name: "name",
}

const filterKeys = ["name", "client_id", "service_id"]

export function Referrals() {
  const [params, setParams] = useSearchParams()

  const setPage = useCallback(
    (nextPage: number) => {
      // no need for page=1 in the URL
      if (nextPage < 2) {
        if (params.get("page")) {
          params.delete("page")
          setParams(params)
        }
      } else {
        const nextPageStr = String(nextPage)

        // only set the param if it differs from the current one
        if (nextPageStr !== params.get("page")) {
          params.set("page", nextPageStr)
          setParams(params)
        }
      }
    },
    [params, setParams],
  )

  const page = useMemo(() => {
    const pageParam = params.get("page")
    return pageParam ? parseInt(pageParam) : undefined
  }, [params])

  const setSort = useCallback(
    (sortState: SortingState) => {
      if (!sortState.length) {
        if (params.get("sort")) {
          params.delete("sort")
          setParams(params)
        }
      } else if (sortState.length) {
        const sortValue = sortState?.[0]
        const sortParam = `${sortParamKey[sortValue.id]}_${
          sortValue.desc ? "desc" : "asc"
        }`

        // only set the param if it differs from the current one
        if (sortParam !== params.get("sort")) {
          params.set("sort", sortParam)
          setParams(params)
        }
      }
    },
    [setParams, params],
  )

  const sort = useMemo(() => {
    const sortParam = params.get("sort")
    return sortParam ? sortParam : undefined
  }, [params])

  const setFilter = useCallback(
    (nextFilter: { key: string; value: string } | undefined) => {
      if (!nextFilter || !nextFilter?.value?.length) {
        if (filterKeys.find((key) => params.get(key))) {
          filterKeys.forEach((key) => params.delete(key))
          if (params.get("page")) {
            params.delete("page")
          }
          setParams(params)
        }
      } else if (nextFilter?.value?.length) {
        if (nextFilter.value !== params.get(nextFilter.key)) {
          filterKeys.forEach((key) => params.delete(key))
          params.set(nextFilter.key, nextFilter.value)
          if (params.get("page")) {
            params.delete("page")
          }
          setParams(params)
        }
      }
    },
    [params, setParams],
  )

  const filter = useMemo(() => {
    return filterKeys.reduce((match, key) => {
      const value = params.get(key)

      if (value) {
        return { key, value }
      }

      return match
    }, undefined as { key: string; value: string } | undefined)
  }, [params])

  const { data } = useGetReferrals({ page, sort, filter })
  const { t } = useTranslation()

  const referrals = data?.referrals
  const paginationMeta = data?.pagination

  return (
    <div className="px-28 py-12">
      <p className="mb-6 [font-family:var(--alias-font-display-lg-font-family)] [font-size:var(--alias-font-display-lg-font-size)]">
        {t("admin.headers.referrals")}
      </p>
      <ReferralTable
        data={referrals ?? []}
        paginationMeta={paginationMeta}
        setPage={setPage}
        initialPage={page}
        setSort={setSort}
        initialSort={sort}
        setFilter={setFilter}
        initialFilter={filter}
      />
    </div>
  )
}
